import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as utils from '@owl-nest/utils'

import * as entity from '@boutique/entities'

import { useProductListing } from '../hooks/useProductListing'
import { SeoLink } from './SeoLink'

type Option = { name: string; value: string; facetValue?: string; canonicalUrl?: string }

export type ArrayFilterProps = {
  data: entity.search.product.Result | undefined
  name: keyof entity.search.product.ArrayFilter
  options: Option[]
  title: string
  open: boolean
  onToggle: () => void
}

export const MAX_LIST_LENGTH = 15

export function ArrayFilter({ data, name, options, title, open, onToggle }: ArrayFilterProps) {
  const { add, remove, hide, selectedOptions, validOptions } = useArrayFilter(name, options, data)

  const [showAll, setShowAll] = React.useState(validOptions.length <= MAX_LIST_LENGTH)

  const displayedOptions = showAll ? validOptions : validOptions.slice(0, MAX_LIST_LENGTH)

  if (hide) {
    return <></>
  }

  return (
    <Accordion
      closedContent={
        selectedOptions.length > 0 && (
          <TagList>
            {selectedOptions.map((option) => {
              return (
                <plume.ClickableTag
                  key={option.value}
                  label={
                    <>
                      {option.name}
                      <plume.glyphs.stroke.Close size={12} />
                    </>
                  }
                  onClick={() => remove(option.value)}
                />
              )
            })}
          </TagList>
        )
      }
      title={`${title}${selectedOptions.length > 0 ? ` (${selectedOptions.length})` : ''} `}
      open={open}
      onToggle={onToggle}
    >
      {displayedOptions.map((option) => {
        const checkbox = (
          <CheckboxField
            key={option.value}
            onChange={() => {
              if (option.isSelected) {
                remove(option.value)
              } else {
                add(option.value)
              }
            }}
            checked={option.isSelected}
            label={option.name}
          />
        )

        if (option.canonicalUrl) {
          return (
            <SeoLink key={option.value} href={option.canonicalUrl}>
              {checkbox}
            </SeoLink>
          )
        }

        return checkbox
      })}

      {!showAll && (
        <SeeMore
          onClick={() => {
            setShowAll(true)
          }}
        >
          + voir plus
        </SeeMore>
      )}
    </Accordion>
  )
}

function useArrayFilter(
  name: keyof entity.search.product.ArrayFilter,
  options: Option[],
  data: entity.search.product.Result | undefined,
) {
  const { setFilter, apply, query } = useProductListing()

  const facet = data?.facets[name] ?? { data: {}, total: 0 }

  let hasNoSelected = true

  const validOptions = options
    .map((option) => {
      const isSelected = query.facetFilters?.[name]?.includes(option.value) ?? false

      if (isSelected) {
        hasNoSelected = false
      }

      const count = facet?.data?.[utils.text.slugify(option.facetValue ?? option.value, '-')] ?? 0

      return {
        ...option,
        isSelected,
        count,
      }
    })
    .filter((option) => {
      if (facet === undefined) {
        return true
      }

      return option.count !== 0 || option.isSelected
    })
    .sort((a, b) => a.value.localeCompare(b.value))

  const selectedOptions = validOptions.filter((option) => option.isSelected)

  return {
    remove,
    add,
    hide:
      (hasNoSelected && facet?.total === 0) ||
      (validOptions.length === 1 && selectedOptions.length === 0) ||
      validOptions.length === 0,
    selectedOptions,
    validOptions,
  }

  function remove(value: string) {
    apply(
      setFilter(
        name,
        selectedOptions.filter((option) => option.value !== value).map((option) => option.value),
      ),
      { scroll: false },
    )
  }

  function add(value: string) {
    apply(setFilter(name, [...selectedOptions.map((option) => option.value), value]), { scroll: false })
  }
}

const Accordion = styled(plume.Accordion)`
  padding: 22px 14px 18px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    padding: 16px 14px 12px;
  }

  ${plume.glyphs.Icon} {
    position: relative;
    top: 2px;
  }

  ${plume.styles.accordion.Title} {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const TagList = styled.div`
  margin: 16px -3px 0;

  ${plume.ClickableTag} {
    display: inline-flex;
    align-items: center;
    margin: 3px;
    color: ${plume.COLORS.PRIMARY_BLACK}; // HACK: Irregular font manipulation
  }

  ${plume.glyphs.stroke.Close} {
    margin-left: 6px;
    color: black;
    margin-top: 2px;
  }
`

const SeeMore = styled(plume.LinkAsButton)`
  ${plume.styles.copy._smallStyle}
  margin-top: 16px;
`

const CheckboxField = styled(plume.CheckboxField)`
  padding-left: 40px;

  &:first-of-type {
    margin-top: 16px;
  }

  ${plume.styles.field.ActualLabel} {
    width: 100%;
    height: 100%;
    cursor: pointer;
    padding: 8px 0 8px 0;
  }

  &:hover {
    background: ${plume.COLORS.BLUE_SHADE_3};
  }

  ${plume.styles.input.HiddenFocusableInput} {
    margin: 8px 0 8px 8px;
  }

  ${plume.ControlledCheckboxWrapper} {
    margin: 8px 0 8px 8px;
    top: 0;
  }

  ${plume.styles.field.Title} {
    ${plume.styles.copy._smallStyle}
    margin-top: -2px;
  }
`
